import { Component } from '@theme/component';
import { fetchConfig, preloadImage, onAnimationEnd, yieldToMainThread } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';
import { createCartRecovery, itemFromFormData, readCart, requestCartJson } from '@theme/prevura-cart-recovery';
import {
  hasPrevuraPackage,
  isPrevuraPackageHandle,
  normalizePrevuraPackageCart,
} from '@theme/prevura-cart-contract';

const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;
let recoveryStorage;
try { recoveryStorage = window.sessionStorage; } catch { /* The cart also works without storage. */ }
const cartRecovery = createCartRecovery({ storage: recoveryStorage, isPackagePresent: hasPrevuraPackage });

function cartText(key, fallback) {
  return Theme.translations[key] || fallback;
}

function withCartDeadline(promise) {
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => reject(new Error('Cart check timed out')), 20000);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer));
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number[] | undefined} */
  #resetTimeouts = /** @type {number[]} */ ([]);

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#resetTimeouts) {
      this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    }
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const productForm = /** @type {ProductFormComponent | null} */ (this.closest('product-form-component'));
    const quantitySelector = productForm?.refs.quantitySelector;
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }
    // A click is only an intention. The form confirms the animation after the
    // server response (or after reconciling a response that was lost).
    this.skipConfirmedAnimation = this.refs.addToCartButton.dataset.puppet === 'true';
  }

  confirmAdded() {
    if (this.skipConfirmedAnimation) {
      this.skipConfirmedAnimation = false;
      return;
    }
    if (this.dataset.addToCartAnimation === 'true' && !this.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
    this.animateAddToCart().catch(() => {});
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    let flyToCartClass = addToCartButton.classList.contains('quick-add__button')
      ? 'fly-to-cart--quick'
      : 'fly-to-cart--main';

    flyToCartElement.classList.add(flyToCartClass);
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart = async function () {
    const { addToCartButton } = this.refs;

    // Initialize the array if it doesn't exist
    if (!this.#resetTimeouts) {
      this.#resetTimeouts = [];
    }

    // Clear all existing timeouts
    this.#resetTimeouts.forEach(/** @param {number} timeoutId */ (timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    // Create new timeout and store it in the array
    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      // Remove this timeout from the array
      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1); 
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {{items: Array<{quantity: number, variant_id: number}>}} Cart
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 * @property {HTMLElement | undefined} volumePricing - The volume pricing component.
 * @property {any | undefined} quantitySelector - The quantity selector component.
 * @property {HTMLElement | undefined} quantitySelectorWrapper - The quantity selector wrapper element.
 * @property {HTMLElement | undefined} quantityLabel - The quantity label element.
 * @property {HTMLElement | undefined} pricePerItem - The price per item component.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #variantChangeInProgress = false;

  /** @type {boolean} */
  #requestInProgress = false;

  /** @type {Array<{variantId: string, quantity: number}>} */
  #addToCartQueue = [];
  #retryUncertain = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) this.#abortController = new AbortController();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Updates quantity selector with cart data for current variant
   * @param {Cart} cart - The cart object with items array
   * @returns {number} The cart quantity for the current variant
   */
  #updateCartQuantityFromData(cart) {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value || !cart?.items) return 0;

    const cartItem = cart.items.find((item) => item.variant_id.toString() === variantIdInput.value.toString());
    const cartQty = cartItem ? cartItem.quantity : 0;

    // Use public API to update quantity selector
    const quantitySelector = /** @type {any | undefined} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.setCartQuantity) {
      quantitySelector.setCartQuantity(cartQty);
    }

    // Update quantity label if it exists
    this.#updateQuantityLabel(cartQty);

    return cartQty;
  }

  /**
   * Fetches the current cart and updates the quantity display.
   * @returns {Promise<Cart | null>} The cart object, or null if fetch fails.
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return null;

    try {
      const cart = await readCart();

      this.#updateCartQuantityFromData(cart);
      return cart;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return null;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    const cart = /** @type {Cart} */ (event.detail?.resource);
    if (cart?.items) {
      this.#updateCartQuantityFromData(cart);
    } else {
      await this.#fetchAndUpdateCartQuantity();
    }
  };

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    if (this.#variantChangeInProgress) {
      const intendedVariantId = this.#getIntendedVariantId();
      const quantity = this.#getQuantity();

      if (intendedVariantId && !this.#addToCartQueue.some((item) => item.variantId === intendedVariantId)) {
        this.#addToCartQueue.push({ variantId: intendedVariantId, quantity });
      }
      this.#setLiveRegionText(cartText('cartWait', 'Dein Warenkorb wird gerade aktualisiert. Bitte warte kurz.'));
      return;
    }

    return this.#processAddToCart(undefined, undefined, event);
  }

  /** @returns {string | undefined} */
  #getIntendedVariantId() {
    return new URL(window.location.href).searchParams.get('variant') || this.refs.variantId?.value || undefined;
  }

  /** @returns {number} */
  #getQuantity() {
    return Number(this.refs.quantitySelector?.getValue?.()) || Number(this.dataset.quantityDefault) || 1;
  }

  /**
   * @param {string} [overrideVariantId]
   * @param {number} [overrideQuantity]
   * @param {Event} [event]
   */
  async #processAddToCart(overrideVariantId, overrideQuantity, event) {
    if (this.#requestInProgress) return;
    const form = this.querySelector('form');
    if (!form) return;
    const containers = Array.from(this.querySelectorAll('add-to-cart-component'));
    if (!overrideVariantId && containers.some((container) => container.refs.addToCartButton?.disabled)) return;
    if (!overrideVariantId && !form.checkValidity()) {
      form.reportValidity();
      return;
    }
    if (!overrideVariantId && this.refs.quantitySelector?.canAddToCart) {
      const validation = this.refs.quantitySelector.canAddToCart();
      if (!validation.canAdd) {
        const message = (this.dataset.quantityErrorMax || cartText('cartAddFailed', 'Bitte prüfe die gewünschte Menge.'))
          .replace('{{ maximum }}', validation.maxQuantity?.toString() || '');
        this.#showCartMessage(message, { focus: true });
        return;
      }
    }
    const formData = new FormData(form);
    if (overrideVariantId) formData.set('id', overrideVariantId);
    if (overrideQuantity !== undefined) formData.set('quantity', String(overrideQuantity));
    const isPackage = isPrevuraPackageHandle(this.dataset.productHandle || '');
    if (isPackage) formData.set('quantity', '1');
    const sectionIds = this.#cartSectionIds();
    if (sectionIds.length) formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);
    const request = fetchConfig('javascript', { body: formData });
    return this.#sendCartOperation({
      form, containers, items: [itemFromFormData(formData)], isPackage, sectionIds, event,
      send: () => requestCartJson(Theme.routes.cart_add_url, request),
    });
  }

  /** Keep queued variant selections on the same recovery/error path as normal adds. */
  async #processBatchAddToCart(queuedItems) {
    if (!queuedItems.length || this.#requestInProgress) return;
    const form = this.querySelector('form');
    if (!form) return;
    const isPackage = isPrevuraPackageHandle(this.dataset.productHandle || '');
    if (isPackage) return this.#processAddToCart(queuedItems[0].variantId, 1);
    const base = itemFromFormData(new FormData(form));
    const items = queuedItems.map((item) => ({ ...base, id: item.variantId, quantity: item.quantity }));
    const sectionIds = this.#cartSectionIds();
    return this.#sendCartOperation({
      form, containers: Array.from(this.querySelectorAll('add-to-cart-component')), items, isPackage, sectionIds,
      send: () => requestCartJson(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds, sections_url: window.location.pathname }),
      }),
    });
  }

  #cartSectionIds() {
    return [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
      .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
  }

  async #sendCartOperation({ form, containers, items, isPackage, sectionIds, event, send }) {
    if (this.#requestInProgress) return;
    this.#requestInProgress = true;
    clearTimeout(this.#timeout);
    this.#hideCartMessage();
    this.setAttribute('aria-busy', 'true');
    this.dataset.cartState = 'pending';
    const pendingText = cartText('cartAdding', 'Wird hinzugefügt …');
    this.#setLiveRegionText(pendingText);
    const buttons = containers.map((container) => container.refs.addToCartButton).filter(Boolean);
    const sticky = this.closest('pv-pdp')?.querySelector('.pv-pdp__sticky-button');
    if (sticky) buttons.push(sticky);
    const states = buttons.map((button) => {
      const text = button.querySelector('.add-to-cart-text__content > span > span') || button;
      const state = { button, text, label: text.textContent, disabled: button.disabled };
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      text.textContent = pendingText;
      return state;
    });
    let result;
    try {
      result = await cartRecovery.run({
        items, send, isPackage, retryUncertain: this.#retryUncertain,
        prepare: isPackage ? async () => {
          const prepared = await withCartDeadline(normalizePrevuraPackageCart({ sectionIds, sectionsUrl: window.location.pathname }));
          return prepared.cart;
        } : undefined,
      });
    } catch {
      result = { kind: 'preflight-error' };
    } finally {
      this.#requestInProgress = false;
      this.removeAttribute('aria-busy');
      for (const state of states) {
        state.text.textContent = state.label;
        state.button.disabled = state.disabled;
        state.button.removeAttribute('aria-busy');
      }
      if (event) cartPerformance.measureFromEvent('add:user-action', event);
    }
    this.#retryUncertain = result.kind === 'uncertain';
    this.dataset.cartState = result.kind;
    if (result.cart) this.#updateCartQuantityFromData(result.cart);
    const data = result.response?.data;
    const sections = data?.sections || result.cart?.sections;
    const eventData = { source: 'product-form-component', productId: this.dataset.productId, itemCount: result.itemCount || 0, sections };
    if (['success', 'recovered', 'already-present'].includes(result.kind)) {
      const message = result.kind === 'already-present'
        ? cartText('cartAlreadyAdded', 'Das Präventionspaket ist bereits einmal im Warenkorb.')
        : result.kind === 'recovered'
          ? cartText('cartAddRecovered', 'Dein vorheriger Versuch war erfolgreich. Bitte prüfe den Warenkorb, bevor du einen weiteren Artikel hinzufügst.')
          : Theme.translations.added;
      this.#setLiveRegionText(message);
      if (result.kind === 'success') containers.forEach((container) => container.confirmAdded?.());
      this.#timeout = setTimeout(() => this.#clearLiveRegionText(), SUCCESS_MESSAGE_DISPLAY_DURATION);
      // Recovery is a cart refresh, never a second add or a second custom purchase signal.
      if (result.kind !== 'success') eventData.source = 'prevura-cart-recovery';
      this.dispatchEvent(new CartAddEvent(result.cart || undefined, form.id || this.id, eventData));
      return result;
    }
    let message;
    if (result.kind === 'busy') message = cartText('cartWait', 'Dein Warenkorb wird gerade aktualisiert. Bitte warte kurz.');
    else if (result.kind === 'uncertain') message = cartText('cartAddUncertain', 'Wir konnten das Hinzufügen noch nicht bestätigen. Bitte prüfe deinen Warenkorb, bevor du es erneut versuchst.');
    else if (result.kind === 'partial') message = cartText('cartAddPartial', 'Bitte prüfe die hinzugefügte Menge im Warenkorb.');
    else if (result.kind === 'preflight-error') message = cartText('cartCheckFailed', 'Der Warenkorb konnte nicht geprüft werden. Bitte prüfe deine Verbindung und versuche es erneut.');
    else message = (typeof data?.description === 'string' && data.description) || cartText('cartAddFailed', 'Das Hinzufügen hat nicht geklappt. Bitte versuche es erneut.');
    this.#showCartMessage(message, { cartLink: result.kind === 'uncertain' || result.kind === 'partial', focus: result.kind !== 'busy' });
    if (result.kind !== 'busy') this.dispatchEvent(new CartErrorEvent(form.id || this.id, message, data?.description, data?.errors));
    if (result.cart) {
      // Update partial additions without opening a drawer over the error message.
      this.dispatchEvent(new CartUpdateEvent(result.cart, form.id || this.id, { ...eventData, didError: true, source: 'prevura-cart-recovery' }));
    }
    return result;
  }

  #hideCartMessage() {
    const error = this.refs.addToCartTextError || this.querySelector('[data-prevura-cart-error]');
    if (error) { error.classList.add('hidden'); error.hidden = true; }
  }

  #showCartMessage(message, { cartLink = false, focus = false } = {}) {
    let error = this.refs.addToCartTextError || this.querySelector('[data-prevura-cart-error]');
    if (!error) {
      error = document.createElement('div');
      error.className = 'product-form-text__error';
      error.setAttribute('data-prevura-cart-error', '');
      this.querySelector('form')?.append(error);
    }
    error.hidden = false;
    error.classList.remove('hidden');
    error.tabIndex = -1;
    let text = error.querySelector('[data-prevura-cart-error-text]');
    if (!text) {
      // Preserve a theme error icon, while replacing only the old message text.
      for (const node of Array.from(error.childNodes)) if (node.nodeType === 3) node.remove();
      text = document.createElement('span');
      text.setAttribute('data-prevura-cart-error-text', '');
      error.append(text);
    }
    text.textContent = message;
    let link = error.querySelector('[data-prevura-cart-error-link]');
    if (!link) {
      link = document.createElement('a');
      link.setAttribute('data-prevura-cart-error-link', '');
      error.append(link);
    }
    link.href = Theme.routes.cart_url;
    link.textContent = cartText('cartView', 'Warenkorb ansehen');
    link.hidden = !cartLink;
    this.#setLiveRegionText(message);
    if (focus && this.isConnected) {
      requestAnimationFrame(() => {
        if (!error.isConnected) return;
        const bounds = error.getBoundingClientRect();
        if (bounds.top < 0 || bounds.bottom > window.innerHeight) error.scrollIntoView({ block: 'center', behavior: 'auto' });
        error.focus({ preventScroll: true });
      });
    }
  }

  /** @param {number} cartQty */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId } = this.refs;
    variantId.value = event.detail.resource?.id ?? '';

    this.#variantChangeInProgress = false;

    if (this.#addToCartQueue.length > 0) {
      const queuedItems = [...this.#addToCartQueue];
      this.#addToCartQueue = [];
      this.#processBatchAddToCart(queuedItems);
    }

    const { addToCartButtonContainer: currentAddToCartButtonContainer, acceleratedCheckoutButtonContainer } = this.refs;
    const currentAddToCartButton = currentAddToCartButtonContainer?.refs.addToCartButton;

    // Update state and text for add-to-cart button
    if (!currentAddToCartButtonContainer || (!currentAddToCartButton && !acceleratedCheckoutButtonContainer)) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      currentAddToCartButtonContainer.disable();
    } else {
      currentAddToCartButtonContainer.enable();
    }

    const newAddToCartButton = event.detail.data.html.querySelector('product-form-component [ref="addToCartButton"]');
    if (newAddToCartButton && currentAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        this.refs.addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          productVariantMedia + '&width=100'
        );
      }
    }

    // Check if quantity rules, price-per-item, or add-to-cart are appearing/disappearing (causes layout shift)
    const {
      quantityRules,
      pricePerItem,
      quantitySelector,
      productFormButtons,
      quantityLabel,
      quantitySelectorWrapper,
    } = this.refs;

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(newQuantityInput.min, newQuantityInput.max || null, newQuantityInput.step);
    }

    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    const newPricePerItem = event.detail.data.html.querySelector('price-per-item');
    const isPricePerItemChanging = !!pricePerItem !== !!newPricePerItem;

    if ((isQuantityRulesChanging || isPricePerItemChanging) && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (productFormButtons && newProductFormButtons) {
        morph(productFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (this.refs.quantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          this.refs.quantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          this.refs.quantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      /** @type {Array<[string, HTMLElement | undefined, HTMLElement | undefined]>} */
      const morphTargets = [
        ['.quantity-label', quantityLabel, quantitySelector],
        ['.quantity-rules', quantityRules, this.refs.productFormButtons],
        ['price-per-item', pricePerItem, quantitySelectorWrapper],
      ];

      for (const [selector, currentElement, fallback] of morphTargets) {
        this.#morphOrUpdateElement(currentElement, event.detail.data.html.querySelector(selector), fallback);
      }
    }

    // Morph volume pricing if it exists
    const currentVolumePricing = this.refs.volumePricing;
    const newVolumePricing = event.detail.data.html.querySelector('volume-pricing');
    this.#morphOrUpdateElement(currentVolumePricing, newVolumePricing, this.refs.productFormButtons);

    const hasB2BFeatures =
      quantityRules || newQuantityRules || pricePerItem || newPricePerItem || currentVolumePricing || newVolumePricing;

    if (!hasB2BFeatures) return;

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /** @param {import('./events').VariantSelectedEvent} _event */
  #onVariantSelected = (_event) => {
    this.#variantChangeInProgress = true;
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

